import React, { useState } from 'react'
import { graphql } from 'gatsby'

// Components
import {
  TitleDefault,
  TitleAlt,
  BannerDefault,
  ContentDefault,
  BoxDefault,
  LineDefault,
} from 'components/Elements'
import CallToAction from 'components/CallToAction'
import Services from 'components/Services'

import SEO from 'components/SEO'
import Layout from 'components/Layout'

import { map as _map } from 'lodash'
import { Nav, Accordion, useAccordionToggle } from 'react-bootstrap'

import 'styles/templates/PageTemplate.scss'
import 'styles/templates/FAQTemplate.scss'

import { ButtonDefault } from '../components/Elements/Elements'
import ParseContent from '../components/ParseContent'

// Icons
import chevronDown from 'img/icon/down-arrow-blue.svg'
import chevronUp from 'img/icon/up-arrow-blue.svg'

export const pageQuery = graphql`
  query ($wordpress_id: Int) {
    page: wordpressPage(wordpress_id: { eq: $wordpress_id }) {
      title
      path
      excerpt
      content
      acf {
        backgroundImage: background_image {
          localFile {
            childImageSharp {
              fluid(quality: 100, maxWidth: 800) {
                ...GatsbyImageSharpFluid_withWebp_noBase64
              }
              fixed(width: 800, toFormat: JPG) {
                src
              }
            }
          }
        }
        packages {
          title
          kosten
          opsommingen
          link {
            title
            url
            target
          }
        }
        callToActionTitle: call_to_action_title
        callToActionSubtitle: call_to_action_subtitle
        callToActionButtonText: call_to_action_button_text
        callToActionButtonLocation: call_to_action_button_location

        faq: groups {
          title
          questions {
            question
            answer
          }
        }
      }
      yoast_title
      yoast_meta {
        name
        content
        property
      }
    }
  }
`

const Page = ({
  data: {
    page: {
      title,
      excerpt,
      path,
      content,
      acf: fields,
      yoast_title,
      yoast_meta,
    },
  },
}) => {
  const [panelIndex, setPanelIndex] = useState()

  const CustomToggle = ({ children, eventKey }) => {
    const customOnClick = useAccordionToggle(eventKey, () => {
      setPanelIndex(eventKey === panelIndex ? null : eventKey)
    })

    const customClass = eventKey === panelIndex ? 'open' : 'closed'

    return (
      <Nav.Link className={`p-0 ${customClass}`} onClick={customOnClick}>
        {children}
      </Nav.Link>
    )
  }

  return (
    <Layout backgroundImage={fields.backgroundImage}>
      {fields.backgroundImage && fields.backgroundImage.localFile ? (
        <SEO
          yoast={{ meta: yoast_meta }}
          pathname={path}
          title={yoast_title}
          description={excerpt}
          image={fields.backgroundImage.localFile.childImageSharp.fixed.src}
          article
        />
      ) : (
        <SEO
          yoast={{ meta: yoast_meta }}
          title={yoast_title}
          description={excerpt}
          pathname={path}
        />
      )}
      <div className="page-template">
        <BannerDefault className="py-5">
          <div className="row">
            <div className="col-12 col-lg-6 py-4">
              <TitleDefault asH1 className="pl-lg-5">
                {title}
              </TitleDefault>
            </div>
            <div className="col-12 col-lg-6" />
          </div>
        </BannerDefault>

        <div className="container pb-5">
          <BoxDefault>
            <ContentDefault className="py-4 px-3 px-lg-5">
              <ParseContent content={content} />
            </ContentDefault>
          </BoxDefault>
        </div>

        {fields.callToActionTitle && (
          <div className="my-5 pb-5">
            <CallToAction
              title={fields.callToActionTitle}
              subtitle={fields.callToActionSubtitle}
              buttonText={fields.callToActionButtonText}
              buttonLocation={fields.callToActionButtonLocation}
            />
          </div>
        )}

        {fields.faq && (
          <div className="mt-5 service container">
            <LineDefault />
            <TitleAlt className="mt-3">Onze</TitleAlt>
            <TitleDefault className="pl-lg-5">Diensten</TitleDefault>
            <Services />
          </div>
        )}

        {fields.faq && (
          <div className="my-5 py-lg-5 service container">
            <TitleDefault className="pl-lg-5 mb-5">FAQ</TitleDefault>
            <BoxDefault>
              <ContentDefault className="px-3 px-lg-5 pt-5">
                {_map(fields.faq, ({ title, questions }, groupIndex) => (
                  <div className="faq mb-5" key={groupIndex}>
                    <TitleAlt>{title}</TitleAlt>
                    <Accordion className="mt-4">
                      {_map(questions, ({ question, answer }, index) => (
                        <div className="mt-3" key={index}>
                          <div>
                            <CustomToggle eventKey={`${groupIndex}-${index}`}>
                              <div className="d-flex justify-content-between w-100 text-left">
                                <div className="font-weight-l">{question}</div>
                                <div>
                                  <img
                                    className="faq-chevron faq-chevron-up"
                                    src={chevronUp}
                                    alt=""
                                  />
                                  <img
                                    className="faq-chevron faq-chevron-down"
                                    src={chevronDown}
                                    alt=""
                                  />
                                </div>
                              </div>
                            </CustomToggle>
                          </div>
                          <Accordion.Collapse
                            eventKey={`${groupIndex}-${index}`}
                          >
                            <div
                              className="pb-3"
                              dangerouslySetInnerHTML={{ __html: answer }}
                            />
                          </Accordion.Collapse>
                        </div>
                      ))}
                    </Accordion>
                  </div>
                ))}
              </ContentDefault>
            </BoxDefault>
          </div>
        )}

        {fields.packages && fields.packages !== undefined && (
          <section id="pakketten">
            <div className="container-xl">
              <div className="row justify-content-center py-5">
                {fields.packages.map((item, index) => (
                  <div className="col-md-6 col-lg-4 packages mb-4" key={index}>
                    <div className="inner">
                      <h3 className="title">{item.title}</h3>
                      <p className="price">{item.kosten}</p>
                      <div
                        dangerouslySetInnerHTML={{ __html: item.opsommingen }}
                      />
                      <div className="cta">
                        <ButtonDefault to={item.link.url} target="_blank">
                          {item.link.title}
                        </ButtonDefault>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  )
}

export default Page
